import React, { useState } from "react";
import { Select, t } from "../../../components/CustomComponent.js";
import { zoneOptions } from "../../../components/location_option.js";
import { bankLogo } from "../../../components/topup_option.js";
import PartnerField from "./PartnerField.js";
import ICON from "../icons.js";
import {
  EXPERIENCE_OPTIONS,
  EXPERTISE_OPTIONS,
  PASSWORD_MIN_LENGTH,
  PICTURE_ACCEPT,
  PICTURE_RECOMMEND,
  fileSize,
  pictureFileError,
} from "../partner_option.js";
import "../../../components/global.js";

/* ชื่อธนาคารมีสองภาษาใน global.bank — เลือกตามภาษาที่ผู้ใช้ตั้งไว้ เหมือนหน้า
   ตั้งค่าเหรียญฝั่งหลังบ้าน (สูตรเดียวกับ HomeProfileModal) */
const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";

/* ============================================================================
 * PartnerProfile — ขั้นที่ 1 : ข้อมูลส่วนตัวของผู้สมัครพาร์ตเนอร์
 *
 * แบ่งเป็น 5 บล็อกตามลำดับที่ผู้ใช้คิด : ติดต่อได้ที่ไหน › อยู่ที่ไหน › ทำอะไรมาบ้าง ›
 * รับเงินเข้าบัญชีไหน › ตั้งรหัสผ่านไว้ใช้ทีหลัง
 *
 * บัญชีรับเงินบังคับกรอก เพราะเป็นบัญชีที่ระบบใช้โอนค่าคอมมิชชั่นกลับให้พาร์ตเนอร์ —
 * เก็บทีหลังแปลว่าทีมการเงินต้องไล่ตามขอทีละราย ส่วนรูปโปรไฟล์ไม่บังคับ เป็นรูปที่
 * ลูกค้าจะเห็นคู่กับประกาศของพาร์ตเนอร์รายนี้ (ถูกยกไปเป็นรูปของบัญชีตอนอนุมัติ)
 *
 * รหัสผ่านอยู่ในขั้นนี้ไม่ใช่ขั้นสุดท้าย เพราะมันเป็นข้อมูลของ "บัญชี" เหมือนอีเมล
 * กับเบอร์โทร ไม่ใช่ขั้นตอนการยืนยันตัวตน — และวางไว้ท้ายสุดของขั้นเพื่อไม่ให้ผู้สมัคร
 * เจอช่องรหัสผ่านเป็นอย่างแรกทั้งที่ยังไม่รู้ว่าจะได้บัญชีไปทำอะไร
 * (บัญชีถูกสร้างจริงตอนผู้ดูแลอนุมัติเท่านั้น ก่อนหน้านั้นรหัสผ่านถูกเก็บเป็น hash
 * ในใบสมัคร ยังล็อกอินไม่ได้)
 *
 * ที่อยู่ใช้ชุดจังหวัด/เขต/แขวง จาก th_province.json ตัวเดียวกับฟอร์มเพิ่มทรัพย์
 * ฝั่งแอดมิน (location_option.js) เพื่อให้ค่าที่เก็บเทียบกันได้ตรง ๆ ไม่ใช่ข้อความ
 * ที่ผู้สมัครพิมพ์เอง และเลือกแขวง/ตำบลแล้วรหัสไปรษณีย์เติมให้อัตโนมัติ
 *
 * ช่องที่เป็นรายการเลือกใช้คอมโพเนนต์ Select ของโปรเจกต์ (พิมพ์ค้นหาในรายการได้
 * ซึ่งจำเป็นมากกับจังหวัด 77 รายการ) — Select วางรายการด้วย position:fixed จึงเพี้ยน
 * ถ้ามี ancestor ตัวใดมี transform/animation/filter ห้ามใส่สิ่งเหล่านี้ให้กล่องที่ครอบ
 * ฟอร์มนี้ (ดูหมายเหตุหัวไฟล์ partner.css)
 *
 * กรอบของช่อง Select ต้องเป็น <div> ไม่ใช่ <label> — คลิกรายการในดรอปดาวน์ที่อยู่ใต้
 * <label> เบราว์เซอร์จะโยนโฟกัสกลับเข้า input ทำให้ดรอปดาวน์เด้งเปิดใหม่ทันทีที่เลือก
 * ========================================================================== */
export default function PartnerProfile({ form, errors = {}, onChange }) {
  const { provinceOptions, districtOptions, subdistrictOptions } = zoneOptions(form.province, form.district);

  /* สลับดู/ซ่อนรหัสผ่านคุมทั้งสองช่องพร้อมกัน — ช่องยืนยันมีไว้กันพิมพ์ผิด
     ถ้าเปิดดูได้ทีละช่องก็ยังเทียบด้วยตาไม่ได้อยู่ดี */
  const [show, setShow] = useState(false);

  /* ข้อผิดพลาดของรูปเก็บไว้ในคอมโพเนนต์ ไม่ส่งขึ้นไปรวมกับ errors ของฟอร์ม —
     มันเกิดจากไฟล์ที่เพิ่งเลือก ไม่ใช่ค่าที่ค้างอยู่ในฟอร์ม การกด "ถัดไป" จึงไม่ควรปลุกมันขึ้นมา
     (รูปไม่บังคับอยู่แล้ว ไฟล์ที่ไม่ผ่านคือไม่ถูกรับไว้ตั้งแต่ต้น ฟอร์มยังส่งได้ตามปกติ) */
  const [pictureError, setPictureError] = useState("");

  const pickPicture = (event) => {
    const file = event.target.files?.[0];
    // เปิดกล่องเลือกไฟล์แล้วกดยกเลิก — รูปเดิมที่เลือกไว้ต้องอยู่เหมือนเดิม
    event.target.value = "";
    if (!file) return;

    const error = pictureFileError(file);
    if (error) return setPictureError(error);

    // object URL ของรูปเก่าต้องคืนก่อนทับ ไม่งั้นค้างในหน่วยความจำจนกว่าจะปิดแท็บ
    if (form.picture?.url) URL.revokeObjectURL(form.picture.url);
    setPictureError("");
    onChange({
      picture: { name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file), file },
    });
  };

  /* ไม่ถามยืนยันก่อนลบต่างจากเอกสารยืนยันตัวตน — รูปโปรไฟล์ไม่บังคับและเลือกใหม่ได้
     ในคลิกเดียว การขวางด้วยป๊อปอัปจึงเป็นภาระมากกว่าเป็นตัวช่วย */
  const removePicture = () => {
    if (form.picture?.url) URL.revokeObjectURL(form.picture.url);
    setPictureError("");
    onChange({ picture: null });
  };

  /* เปลี่ยนจังหวัด = เขต/แขวง/รหัสไปรษณีย์เดิมใช้ไม่ได้แล้ว ต้องล้างพร้อมกัน
     ไม่งั้นจะได้ที่อยู่ที่ผสมข้ามจังหวัดโดยผู้กรอกไม่ทันสังเกต */
  const setProvince = (province) => onChange({ province, district: "", subdistrict: "", postcode: "" });
  const setDistrict = (district) => onChange({ district, subdistrict: "", postcode: "" });

  /* code ใน th_province.json คือรหัสไปรษณีย์ของแขวง/ตำบลนั้น — เติมให้เลย
     แต่ยังแก้เองได้ เพราะบางตำบลใหญ่ใช้มากกว่าหนึ่งรหัส */
  const setSubdistrict = (value, option) => {
    const found = option?.code ? option : subdistrictOptions.find((item) => item.value === value);
    onChange({ subdistrict: value, postcode: found?.code ? String(found.code) : "" });
  };

  const toggleExpertise = (value) => {
    const list = form.expertise || [];
    onChange({ expertise: list.includes(value) ? list.filter((item) => item !== value) : [...list, value] });
  };

  return (
    <>
      <div className="hm-pt-block__title">{t("partner_section_personal")}</div>
      <div className="hm-pt-grid">
        {/* รูปโปรไฟล์อยู่หัวบล็อกเพราะเป็นสิ่งที่ผู้สมัคร "เห็นว่าเป็นตัวเอง" ก่อนช่องอื่น
            as="div" ไม่ใช่ <label> — ข้างในมีทั้งพื้นที่เลือกไฟล์และปุ่มลบ ถ้าเป็น
            <label> การกดปุ่มลบจะไปเปิดกล่องเลือกไฟล์ตามไปด้วย */}
        <PartnerField
          as="div"
          label={t("partner_picture")}
          optional
          hint={t("partner_picture_hint").replace("{size}", PICTURE_RECOMMEND)}
          error={pictureError}
          full
        >
          <div className="hm-pt-avatar">
            <label className={`hm-pt-avatar__pick ${form.picture ? "is-filled" : ""}`}>
              <input type="file" accept={PICTURE_ACCEPT} onChange={pickPicture} />
              {form.picture?.url ? (
                <img src={form.picture.url} alt="" className="hm-pt-avatar__img" />
              ) : (
                <span className="hm-ico hm-pt-avatar__ico">{ICON.camera}</span>
              )}
            </label>

            <div className="hm-pt-avatar__side">
              {form.picture ? (
                <>
                  <b className="hm-pt-avatar__name">{form.picture.name}</b>
                  <em className="hm-pt-avatar__meta">{fileSize(form.picture.size)}</em>
                  <div className="hm-pt-avatar__actions">
                    {/* เป็น <label> ที่มี input[type=file] ของตัวเองซ่อนอยู่ ไม่ใช่ <button> —
                        ปุ่มจริงเปิดกล่องเลือกไฟล์ไม่ได้ถ้าไม่เรียก .click() ใส่ ref
                        (กดที่กรอบรูปก็เปลี่ยนรูปได้เหมือนกัน อันนี้มีไว้ให้เห็นว่ากดได้) */}
                    <label className="hm-pt-avatar__btn">
                      <input type="file" accept={PICTURE_ACCEPT} onChange={pickPicture} />
                      <span className="hm-ico">{ICON.image}</span>
                      {t("partner_picture_change")}
                    </label>
                    <button type="button" className="hm-pt-avatar__btn hm-pt-avatar__btn--remove" onClick={removePicture}>
                      <span className="hm-ico">{ICON.close}</span>
                      {t("partner_picture_remove")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <b className="hm-pt-avatar__name">{t("partner_picture_upload")}</b>
                  <em className="hm-pt-avatar__meta">{t("partner_picture_desc")}</em>
                </>
              )}
            </div>
          </div>
        </PartnerField>

        <PartnerField label={t("partner_name")} required error={errors.name}>
          <input
            type="text"
            className="hm-pt-input"
            placeholder={t("partner_name_placeholder")}
            value={form.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </PartnerField>

        <PartnerField label={t("partner_email")} required error={errors.email} hint={t("partner_email_hint")}>
          <input
            type="email"
            className="hm-pt-input"
            placeholder={t("partner_email_placeholder")}
            value={form.email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
        </PartnerField>

        <PartnerField label={t("partner_phone")} required error={errors.phone} hint={t("partner_phone_hint")}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            className="hm-pt-input"
            placeholder={t("partner_phone_placeholder")}
            value={form.phone}
            /* รับเฉพาะตัวเลข — เบอร์นี้ถูกใช้ส่ง OTP ต่อในขั้นถัดไป
               แก้เบอร์เมื่อไรถือว่าการยืนยันเดิมเป็นโมฆะ (จัดการที่หน้าแม่) */
            onChange={(e) => onChange({ phone: e.target.value.replace(/[^0-9]/g, "") })}
          />
        </PartnerField>

        <PartnerField label={t("partner_line")} required error={errors.line_id} hint={t("partner_line_hint")}>
          <input
            type="text"
            className="hm-pt-input"
            placeholder={t("partner_line_placeholder")}
            value={form.line_id}
            onChange={(e) => onChange({ line_id: e.target.value })}
          />
        </PartnerField>
      </div>

      <div className="hm-pt-block__title">{t("partner_section_address")}</div>
      <div className="hm-pt-grid">
        <PartnerField label={t("partner_address")} required error={errors.address} full>
          <input
            type="text"
            className="hm-pt-input"
            placeholder={t("partner_address_placeholder")}
            value={form.address}
            onChange={(e) => onChange({ address: e.target.value })}
          />
        </PartnerField>

        <PartnerField as="div" label={t("partner_province")} required error={errors.province}>
          <Select
            className="hm-pt-input hm-pt-select"
            default={false}
            placeholder={t("partner_select_placeholder")}
            value={form.province}
            options={provinceOptions}
            onChange={setProvince}
          />
        </PartnerField>

        <PartnerField as="div" label={t("partner_district")} required error={errors.district}>
          <Select
            className="hm-pt-input hm-pt-select"
            default={false}
            placeholder={t("partner_select_placeholder")}
            value={form.district}
            options={districtOptions}
            disabled={!form.province}
            onChange={setDistrict}
          />
        </PartnerField>

        {/* Select ส่งตัวเลือกทั้งก้อนมาเป็นอาร์กิวเมนต์ที่สอง setSubdistrict จึงหยิบ
            code (รหัสไปรษณีย์) จากตรงนั้นได้เลย ไม่ต้องวนหาในรายการซ้ำ */}
        <PartnerField as="div" label={t("partner_subdistrict")} required error={errors.subdistrict}>
          <Select
            className="hm-pt-input hm-pt-select"
            default={false}
            placeholder={t("partner_select_placeholder")}
            value={form.subdistrict}
            options={subdistrictOptions}
            disabled={!form.district}
            onChange={setSubdistrict}
          />
        </PartnerField>

        <PartnerField label={t("partner_postcode")} required error={errors.postcode}>
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            className="hm-pt-input"
            placeholder="10110"
            value={form.postcode}
            onChange={(e) => onChange({ postcode: e.target.value.replace(/[^0-9]/g, "") })}
          />
        </PartnerField>
      </div>

      <div className="hm-pt-block__title">{t("partner_section_experience")}</div>
      <div className="hm-pt-grid">
        <PartnerField as="div" label={t("partner_experience_level")} required error={errors.experience}>
          <Select
            className="hm-pt-input hm-pt-select"
            default={false}
            placeholder={t("partner_select_placeholder")}
            value={form.experience}
            options={EXPERIENCE_OPTIONS.map((item) => ({ value: item.value, label: t(item.label) }))}
            onChange={(value) => onChange({ experience: value })}
          />
        </PartnerField>

        {/* ใบอนุญาตนายหน้าไม่บังคับ — ผู้สมัครมือใหม่ยังไม่มี แต่ถ้ามีจะช่วยให้
            ผู้ดูแลอนุมัติได้เร็วขึ้น จึงบอกไว้ใต้ช่อง */}
        <PartnerField label={t("partner_license")} optional hint={t("partner_license_hint")} error={errors.license}>
          <input
            type="text"
            className="hm-pt-input"
            placeholder={t("partner_license_placeholder")}
            value={form.license}
            onChange={(e) => onChange({ license: e.target.value })}
          />
        </PartnerField>

        <PartnerField
          as="div"
          label={t("partner_expertise")}
          optional
          hint={t("partner_expertise_hint")}
          error={errors.expertise}
          full
        >
          <div className="hm-pt-chips">
            {EXPERTISE_OPTIONS.map((item) => {
              const on = (form.expertise || []).includes(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  aria-pressed={on}
                  className={`hm-pt-chip ${on ? "is-active" : ""}`}
                  onClick={() => toggleExpertise(item.value)}
                >
                  {t(item.label)}
                </button>
              );
            })}
          </div>
        </PartnerField>

        <PartnerField label={t("partner_about")} optional error={errors.about} full>
          <textarea
            className="hm-pt-input hm-pt-textarea"
            rows={4}
            maxLength={500}
            placeholder={t("partner_about_placeholder")}
            value={form.about}
            onChange={(e) => onChange({ about: e.target.value })}
          />
        </PartnerField>
      </div>

      <div className="hm-pt-block__title">{t("partner_section_bank")}</div>
      {/* บอกก่อนว่าเอาไปใช้ทำอะไร — ใบสมัครที่ถามเลขบัญชีโดยไม่บอกเหตุผลทำให้คนกรอกลังเล */}
      <p className="hm-pt-note">{t("partner_bank_note")}</p>
      <div className="hm-pt-grid">
        <PartnerField as="div" label={t("partner_bank")} required error={errors.bank}>
          <Select
            className="hm-pt-input hm-pt-select"
            default={false}
            placeholder={t("partner_select_placeholder")}
            value={form.bank}
            /* front = โลโก้ธนาคาร ชุดเดียวกับหน้าตั้งค่าเหรียญฝั่งหลังบ้าน — คนหาธนาคาร
               จากสีและโลโก้เจอเร็วกว่าอ่านชื่อทีละบรรทัด ธนาคารที่ยังไม่มีไฟล์โลโก้
               ให้ปล่อยว่าง ดีกว่าใส่ <img src=""> ซึ่งเบราว์เซอร์ขึ้นเป็นรูปเสียคาช่อง */
            options={(global.bank || []).map((item) => {
              const logo = bankLogo(item.value);
              return {
                value: item.value,
                label: isTh() ? item.name_th : item.label,
                front: logo ? <img className="hm-pt-bank__logo" src={logo} alt="" /> : null,
              };
            })}
            onChange={(value) => onChange({ bank: value || "" })}
          />
        </PartnerField>

        <PartnerField
          label={t("partner_account_no")}
          required
          error={errors.bank_account_no}
          hint={t("partner_account_no_hint")}
        >
          <input
            type="text"
            inputMode="numeric"
            maxLength={30}
            className="hm-pt-input"
            placeholder="123-4-56789-0"
            value={form.bank_account_no}
            /* รับเฉพาะตัวเลขกับขีด — คนคัดลอกเลขบัญชีมาตามรูปแบบที่เห็นในสมุด/แอปธนาคาร
               จึงไม่ตัดขีดทิ้ง แต่ตัวอักษรอื่นไม่มีทางเป็นส่วนหนึ่งของเลขบัญชี */
            onChange={(e) => onChange({ bank_account_no: e.target.value.replace(/[^0-9-]/g, "") })}
          />
        </PartnerField>

        <PartnerField
          label={t("partner_account_name")}
          required
          error={errors.bank_account_name}
          hint={t("partner_account_name_hint")}
          full
        >
          <input
            type="text"
            maxLength={150}
            className="hm-pt-input"
            placeholder={t("partner_account_name_placeholder")}
            value={form.bank_account_name}
            onChange={(e) => onChange({ bank_account_name: e.target.value })}
          />
        </PartnerField>
      </div>

      <div className="hm-pt-block__title">{t("partner_section_password")}</div>
      {/* บอกก่อนว่าเอาไปใช้ตอนไหน ไม่งั้นผู้สมัครจะงงว่าทำไมใบสมัครขอตั้งรหัสผ่าน */}
      <p className="hm-pt-note">{t("partner_password_note")}</p>
      <div className="hm-pt-grid">
        <PartnerField
          label={t("partner_password")}
          required
          error={errors.password}
          hint={t("partner_password_hint").replace("{n}", PASSWORD_MIN_LENGTH)}
        >
          <span className="hm-pt-pass">
            <input
              type={show ? "text" : "password"}
              className="hm-pt-input"
              autoComplete="new-password"
              placeholder={t("partner_password_placeholder")}
              value={form.password}
              onChange={(e) => onChange({ password: e.target.value })}
            />
            {/* ปุ่มดูรหัสผ่าน — ฟอร์มนี้ให้พิมพ์รหัสใหม่สองครั้ง คนที่พิมพ์ไม่ตรงกัน
                ซ้ำ ๆ จะติดอยู่ตรงนี้ถ้ามองไม่เห็นว่าตัวเองพิมพ์อะไรไป */}
            <button
              type="button"
              className="hm-pt-pass__eye"
              aria-label={t("partner_password")}
              aria-pressed={show}
              onClick={() => setShow(!show)}
            >
              <span className="hm-ico">{show ? ICON.eye_slash : ICON.eye}</span>
            </button>
          </span>
        </PartnerField>

        <PartnerField label={t("partner_password_confirm")} required error={errors.password_confirm}>
          <input
            type={show ? "text" : "password"}
            className="hm-pt-input"
            autoComplete="new-password"
            placeholder={t("partner_password_confirm_placeholder")}
            value={form.password_confirm}
            onChange={(e) => onChange({ password_confirm: e.target.value })}
          />
        </PartnerField>
      </div>
    </>
  );
}
